/**
 * Colours that carry meaning on the bench: the gradient rod's palettes, and the
 * colour of light of a given wavelength.
 *
 * The rod's palettes are not rainbows. A rainbow has stretches (the greens)
 * that read as one colour to many students with colour-vision deficiency; each
 * palette here runs between two ends that stay distinct under the common ones,
 * and the ends are ALSO labelled A and B, so colour is never the only cue.
 */

type RGB = [number, number, number];

const PALETTES: Record<string, RGB[]> = {
    // Each palette's first stop is kept well off black: the bench is dark by
    // default, and an end drawn in navy or charcoal disappears into it.
    // blue → teal → amber
    deep: [[72, 110, 225], [44, 140, 200], [65, 182, 196], [253, 174, 97], [230, 120, 20]],
    // teal → coral
    sea: [[26, 140, 128], [53, 160, 150], [150, 215, 205], [246, 182, 150], [214, 96, 77]],
    // grey → white, for print or a projector that washes colour out
    mono: [[100, 104, 118], [140, 143, 156], [178, 180, 192], [214, 216, 224], [248, 249, 252]],
};

export const PALETTE_KEYS = Object.keys(PALETTES);

const hex = ([r, g, b]: RGB): string =>
    `#${[r, g, b].map(c => Math.round(c).toString(16).padStart(2, '0')).join('')}`;

/** The rod's colour a fraction `t` of the way from end A to end B. */
export function rodColour(palette: string, t: number): string {
    const stops = PALETTES[palette] ?? PALETTES.deep;
    const x = Math.min(1, Math.max(0, t)) * (stops.length - 1);
    const i = Math.min(stops.length - 2, Math.floor(x));
    const f = x - i;
    const a = stops[i], b = stops[i + 1];
    return hex([a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f, a[2] + (b[2] - a[2]) * f]);
}

/**
 * Light of wavelength λ (nm), as a display colour. The usual piecewise fit,
 * dimmed at the ends of the visible range where the eye is least sensitive.
 * 589 nm comes out the sodium amber a student has seen in a street lamp.
 */
export function wavelengthColour(nm: number): string {
    let r = 0, g = 0, b = 0;
    if (nm < 440) { r = (440 - nm) / 60; b = 1; }
    else if (nm < 490) { g = (nm - 440) / 50; b = 1; }
    else if (nm < 510) { g = 1; b = (510 - nm) / 20; }
    else if (nm < 580) { r = (nm - 510) / 70; g = 1; }
    else if (nm < 645) { r = 1; g = (645 - nm) / 65; }
    else { r = 1; }
    const edge = nm < 420 ? 0.3 + (0.7 * (nm - 380)) / 40 : nm > 680 ? 0.3 + (0.7 * (780 - nm)) / 100 : 1;
    const k = Math.max(0.3, Math.min(1, edge)) * 255;
    return hex([r * k, g * k, b * k]);
}

/** Relative luminance (WCAG), for a colour's contrast against the bench. */
function luminance([r, g, b]: RGB): number {
    const lin = (c: number): number => {
        const v = c / 255;
        return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** The light theme's bench (`--ob-ground`), which a ray must stand out from. */
const LIGHT_GROUND: RGB = [244, 247, 251];
/** Above WCAG's 3:1 for graphics: a ray is a thin line. */
const RAY_CONTRAST = 3.5;

/**
 * How light of a wavelength is DRAWN on a bench of the given theme. On the
 * dark bench, as it is. On the light one a pure spectral colour can all but
 * vanish — sodium yellow on near-white — so it is darkened, keeping its hue,
 * only as far as it takes to stand out: yellow becomes amber, violet barely
 * changes. The wavelength itself, and every number, stay as they are.
 */
/** White light as drawn: near-white on the dark bench, slate on the light one,
 *  where white would vanish into the ground. */
export function whiteInk(theme: 'light' | 'dark' = 'dark'): string {
    return theme === 'light' ? '#475569' : '#f1f5f9';
}

/** The swatch for White on a colour button: the three colours it is traced as. */
export const WHITE_SWATCH = 'conic-gradient(#e5484d 0 33%, #30a46c 0 66%, #8e4ec6 0)';

export function wavelengthInk(nm: number, theme: 'light' | 'dark' = 'dark'): string {
    const c = wavelengthColour(nm);
    if (theme === 'dark') return c;
    const rgb = [1, 3, 5].map(i => parseInt(c.slice(i, i + 2), 16)) as RGB;
    const ground = luminance(LIGHT_GROUND);
    let k = 1;
    while (k > 0.05 && (ground + 0.05) / (luminance(rgb.map(v => v * k) as RGB) + 0.05) < RAY_CONTRAST) k -= 0.02;
    return hex(rgb.map(v => v * k) as RGB);
}
